import React, { useEffect, useMemo, useRef, useState, lazy, Suspense } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { chatbot_initialize, delete_session } from '../../services';
import { Dropdown, DropdownButton } from 'react-bootstrap';
import { useContext } from 'react';
import { PlanContext } from '../../Components/DashboardLayout';
import PlanLockWrapper from '../../Components/PlanLockWrapper';
import { local } from '../../services/config/settings/env';
import AIAdoptionPortal from '../../AIAdoptionPortal/AIAdoptionPortal';
import AIAdoptionWidget from '../../AIAdoption/AIAdoptionWidget';


// Lazy loaded components
const DigitalFootprint = lazy(() => import('../DigitalFootprint/DigitalFootprint'));
const VulneralityManagement = lazy(() => import('../VulneralityManagement/VulneralityManagement'));
const ExposureMap = lazy(() => import('../ExposureMap/ExposureMap'));
const NmtAI = lazy(() => import('../../NmtAI/Nmtai'));
const AddOns = lazy(() => import('../AddOns/AddOns'));
const InternalAudit = lazy(() => import('../Dashboard_InternalAudit/AuditMain'));
const ThirdPartyExternalExposure = lazy(() => import('../ThirdpartyExternalExposure/ThirdPartyExternalExposure'));


const MODULE_SECTIONS = {
    'risk-registry': 'id6',
    'third-party': 'id5',
};

const VIEW_PRESETS = {
    easm: ['id2', 'id3', 'id9'],
    compliance: ['id7', 'id10', 'id6', 'id4', 'id8', 'id9'],
};
const RiskRegistry = lazy(() => import('../RiskRegistry/RiskRegistry'));
const UserProfileUpdateModal = lazy(() => import('../../Auth/UserProfileUpdateModal'));
const PolicyCenter = lazy(() => import('../PolicyCenter/PolicyCenter'));
const ComplianceReady = lazy(() => import('../ComplianceReady/ComplianceReady'));

function Tabs({ onCardSelect }) {
    const location = useLocation();
    const navigate = useNavigate();
    const [activeTab, setActiveTab] = useState('id1');
    const [aiInitializedShow, setAiInitializedShow] = useState(false);
    const [aiInitialized, setAiInitialized] = useState([]);
    const [userProfileUpdateModal, setUserProfileUpdateModal] = useState(false);
    const [remainingCredits, setRemainingCredits] = useState(null);

    // Get plan details
    const { planInfo, permissions } = useContext(PlanContext) || {};
    const isFreePlan = permissions?.isFreePlan || false;
    const TOGGLED_ADDONS_STORAGE_KEY = 'dashboard_toggled_addons';
    const DEFAULT_TOGGLED_ADDONS = {
        id1: true,  // Overview
        id2: true,  // External Attack Surface
        id3: true,  // Assets Inventory
        id5: true,  // Third-Party Risk Management
        id7: true,  // Compliance
        id6: true,  // Risk Registry
        id4: true,  // Vulnerability & Patch Management
        id8: true,  // Governance
        id9: true,  // Governance
        id10: true, // Audit Platform
        id11: true, // AI Governance
    };
    const [toggledAddOns, setToggledAddOns] = useState(() => {
        try {
            const raw = localStorage.getItem(TOGGLED_ADDONS_STORAGE_KEY);
            if (!raw) return DEFAULT_TOGGLED_ADDONS;
            const parsed = JSON.parse(raw);
            // Merge with defaults so newly-added tabs default to visible
            // even when the user has an older saved snapshot.
            return { ...DEFAULT_TOGGLED_ADDONS, ...parsed };
        } catch {
            return DEFAULT_TOGGLED_ADDONS;
        }
    });

    useEffect(() => {
        try { localStorage.setItem(TOGGLED_ADDONS_STORAGE_KEY, JSON.stringify(toggledAddOns)); }
        catch {}
    }, [toggledAddOns]);

    const viewPreset = new URLSearchParams(location.search).get('view');
    const effectiveToggled = useMemo(() => {
        const allow = VIEW_PRESETS[viewPreset];
        if (!allow) return toggledAddOns;
        const out = {};
        Object.keys(toggledAddOns).forEach((k) => { out[k] = allow.includes(k); });
        return out;
    }, [toggledAddOns, viewPreset]);

    useEffect(() => {
        const isPopupClosed = localStorage.getItem("is_popup_closed");
        if (isPopupClosed !== "true") {
            setUserProfileUpdateModal(true);
        }
    }, []);

    const sectionsRef = {
        id1: useRef(null),
        id2: useRef(null),
        id3: useRef(null),
        id5: useRef(null),
        id7: useRef(null),
        id6: useRef(null),
        id4: useRef(null),
        id8: useRef(null),
        id9: useRef(null),
        id10: useRef(null),
        id11: useRef(null),

    };

    useEffect(() => {
        const params = new URLSearchParams(location.search);
        const sectionId = MODULE_SECTIONS[params.get('module')];
        if (!sectionId) return undefined;

        const timer = setTimeout(() => {
            sectionsRef[sectionId]?.current?.scrollIntoView({ behavior: 'smooth' });
            setActiveTab(sectionId);
        }, 800);
        return () => clearTimeout(timer);
    }, [location.search]);

    const delete_chat_session = async () => {
        return await delete_session();
    };

    useEffect(() => {
        const handle_chatbot_initialize = async () => {
            try {
                delete_chat_session();
                const response = await chatbot_initialize();

                const data = response?.statusCode?.response_data?.data;

                const statusCode = response?.statusCode?.response_data?.status_code;
                if (statusCode === 200) {
                    setAiInitialized(data);
                    setTimeout(() => {
                        setAiInitializedShow(true);
                    }, 2000);
                }
            } catch (error) {
                console.error('Error initializing chatbot:', error);
            }
        };
        handle_chatbot_initialize();
    }, []);

    useEffect(() => {
        const observerOptions = {
            root: null,
            threshold: 0.4,
        };

        const observer = new IntersectionObserver((entries) => {
            entries.forEach((entry) => {
                if (entry.isIntersecting) {
                    // Only set active tab if it's currently visible (toggled on)
                    const tabId = entry.target.id;
                    if (effectiveToggled[tabId]) {
                        setActiveTab(tabId);
                    }
                }
            });
        }, observerOptions);

        // Only observe sections that are toggled on
        Object.keys(sectionsRef).forEach((id) => {
            const element = sectionsRef[id]?.current;
            if (element && effectiveToggled[id]) {
                observer.observe(element);
            }
        });

        return () => {
            Object.keys(sectionsRef).forEach((id) => {
                const element = sectionsRef[id]?.current;
                if (element) observer.unobserve(element);
            });
        };
    }, [aiInitializedShow, effectiveToggled]);

    const handleScroll = (e, ref) => {
        e.preventDefault();
        if (ref?.current) {
            ref.current.scrollIntoView({ behavior: 'smooth' });
        }
    };

    const filterModules = [
        {
            "title": "FORESEE AI",
            "key": "id1"
        },
        {
            "title": "EASM",
            "key": "id2"
        },
        {
            "title": "Assets Inventory",
            "key": "id3"
        },
        {
            "title": "TPCRM",
            "key": "id5"
        },
        {
            "title": "Compliance",
            "key": "id7"
        },
        {
            "title": "Audit Platform",
            "key": "id10"
        },
        {
            "title": "Risk Registry",
            "key": "id6"
        },
        {
            "title": "AI Governance",
            "key": "id11"
        },
        {
            "title": "Vulnerability & Patch Management",
            "key": "id4"
        },
        {
            "title": "Policy Center",
            "key": "id8"
        },
        {
            "title": "Add-on Services",
            "key": "id9"
        },

    ];
    useEffect(() => {
        if (aiInitialized) {
            setRemainingCredits(aiInitialized.remaining_credits ?? 0);
        }
    }, [aiInitialized]);

    console.log(">>>>>", localStorage.getItem('activeTab'));

    // Toggle service and update visibility
    const handleToggle = (addOnKey) => {
        setToggledAddOns((prev) => {
            const updated = { ...prev, [addOnKey]: !prev[addOnKey] };
           const res =  localStorage.setItem('activeTab', updated);
            console.log("updated", updated, res);
            

            // If we're hiding the currently active tab, switch to the first visible tab
            if (!updated[addOnKey] && activeTab === addOnKey) {
                // Find first visible tab
                const firstVisibleTab = Object.keys(updated).find(key => updated[key]);
                console.log("okok", firstVisibleTab);
                
                if (firstVisibleTab) {
                    setActiveTab(firstVisibleTab);
                    // local.setItem('activeTab', firstVisibleTab);
                }
            }

            return updated;
        });
    };

    return (
        <div className='cstm_dashboard_tabs'>
            <div className='d-flex align-items-baseline justify-content-between'>
                <ul className="tab-navigation sticky-tab">
                    {aiInitializedShow && effectiveToggled.id1 && (
                        <li>
                            <a
                                href='#id1'
                                onClick={(e) => handleScroll(e, sectionsRef.id1)}
                                className={activeTab === 'id1' ? 'active' : ''}
                            >
                                FORESEE AI
                            </a>
                        </li>
                    )}
                    {effectiveToggled.id2 && (
                        <li>
                            <a
                                href='#id2'
                                onClick={(e) => handleScroll(e, sectionsRef.id2)}
                                className={activeTab === 'id2' ? 'active' : ''}
                            >
                                EASM
                            </a>
                        </li>
                    )}
                    {effectiveToggled.id3 && (
                        <li>
                            <a
                                href='#id3'
                                onClick={(e) => handleScroll(e, sectionsRef.id3)}
                                className={activeTab === 'id3' ? 'active' : ''}
                            >
                                Assets Inventory
                            </a>
                        </li>
                    )}
                    {effectiveToggled.id5 && (
                        <li>
                            <a
                                href='#id5'
                                onClick={(e) => handleScroll(e, sectionsRef.id5)}
                                className={activeTab === 'id5' ? 'active' : ''}
                            >
                                TPCRM
                            </a>
                        </li>
                    )}
                    {/* {effectiveToggled.id7 && (
                        <li>
                            <a
                                href='#id7'
                                onClick={(e) => handleScroll(e, sectionsRef.id7)}
                                className={activeTab === 'id7' ? 'active' : ''}
                            >
                                Compliance
                            </a>
                        </li>
                    )} */}
                    {effectiveToggled.id10 && (
                        <li>
                            <a
                                href='#id10'
                                onClick={(e) => handleScroll(e, sectionsRef.id10)}
                                className={activeTab === 'id10' ? 'active' : ''}
                            >
                                Audit Platform
                            </a>
                        </li>
                    )}
                    {effectiveToggled.id6 && (
                        <li>
                            <a
                                href='#id6'
                                onClick={(e) => handleScroll(e, sectionsRef.id6)}
                                className={activeTab === 'id6' ? 'active' : ''}
                            >
                                Risk Registry
                            </a>
                        </li>
                    )}
                   
                    {/* {effectiveToggled.id11 && (
                        <li>
                            <a
                                href='#id11'
                                onClick={(e) => handleScroll(e, sectionsRef.id11)}
                                className={activeTab === 'id11' ? 'active' : ''}
                            >
                                AI Governance
                            </a>
                        </li>
                    )} */}
                    {/* {effectiveToggled.id4 && (
                        <li>
                            <a
                                href='#id4'
                                onClick={(e) => handleScroll(e, sectionsRef.id4)}
                                className={activeTab === 'id4' ? 'active' : ''}
                            >
                                Vulnerability & Patch Management
                            </a>
                        </li>
                    )} */}
                    {/* {effectiveToggled.id8 && (
                        <li>
                            <a
                                href='#id8'
                                onClick={(e) => handleScroll(e, sectionsRef.id8)}
                                className={activeTab === 'id8' ? 'active' : ''}
                            >
                                Policy Center
                            </a>
                        </li>
                    )} */}
                    {effectiveToggled.id9 && (
                        <li>
                            <a
                                href='#id9'
                                onClick={(e) => handleScroll(e, sectionsRef.id9)}
                                className={activeTab === 'id9' ? 'active' : ''}
                            >
                                Add-on Services
                            </a>
                        </li>
                    )}
                    {/* <li>
                        <a
                            href='/audit-portal'
                            onClick={(e) => { e.preventDefault(); navigate('/audit-portal'); }}
                        >
                            Audit Platform
                        </a>
                    </li> */}

                </ul>
                {/* <div className="d-flex align-items-center gap-2">
                    <DropdownButton
                        className="cstm_filter_dropdown"
                        id="dropdown-flat"
                        title={'Filter'}
                    >
                        <div className="cstm_filter_dropdown_items" style={{ minWidth: 240 }}>
                            {filterModules.map((item, idx) => (
                                <div key={idx} className="dropdown-item-wrapper">
                                    <Dropdown.Item
                                        style={{ cursor: "pointer", padding: "10px 12px" }}
                                    >
                                        <div className="d-flex align-items-center justify-content-between">
                                            <div className="d-flex align-items-center justify-content-between gap-2 w-100">
                                                <span className="d-flex align-items-center gap-2">
                                                    {item.title}
                                                </span>
                                                <div
                                                    className={`toggle-switch ${toggledAddOns[item.key] ? "on" : "off"}`}
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        handleToggle(item.key);
                                                    }}
                                                >
                                                    <div className="toggle-thumb" />
                                                </div>
                                            </div>
                                        </div>
                                    </Dropdown.Item>
                                </div>
                            ))}
                        </div>
                    </DropdownButton>
                </div> */}
            </div>
            <div className="tab-contents">
                {aiInitializedShow && effectiveToggled.id1 && (
                    <div className='scroll_section' id="id1" ref={sectionsRef.id1}>
                        <Suspense fallback={<div></div>}>
                            <NmtAI initializedData={aiInitialized}
                                remainingCredits={remainingCredits}
                                onCreditsUpdate={setRemainingCredits} />
                        </Suspense>
                    </div>
                )}
                {effectiveToggled.id2 && (
                    <div className='scroll_section' id="id2" ref={sectionsRef.id2}>
                        <Suspense fallback={<div></div>}>
                            <ExposureMap onCardSelect={onCardSelect} />
                        </Suspense>
                    </div>
                )}
                {effectiveToggled.id3 && (
                    <div className='scroll_section' id="id3" ref={sectionsRef.id3}>
                        <Suspense fallback={<div></div>}>
                            <DigitalFootprint />
                        </Suspense>
                    </div>
                )}
                {effectiveToggled.id5 && (
                    <div className='scroll_section' id="id5" ref={sectionsRef.id5}>
                        <PlanLockWrapper isLocked={isFreePlan}>
                            <Suspense fallback={<div></div>}>
                                <ThirdPartyExternalExposure />
                            </Suspense>
                        </PlanLockWrapper>
                    </div>
                )}
                {/* {effectiveToggled.id7 && (
                    <div className='scroll_section' id="id7" ref={sectionsRef.id7}>
                        <Suspense fallback={<div></div>}>
                            <ComplianceReady />
                        </Suspense>
                    </div>
                )} */}
                {effectiveToggled.id10 && (
                    <div className='scroll_section' id="id10" ref={sectionsRef.id10}>
                        <Suspense fallback={<div></div>}>
                            <InternalAudit />
                        </Suspense>
                    </div>
                )}
                {effectiveToggled.id6 && (
                    <div className='scroll_section' id="id6" ref={sectionsRef.id6}>
                        <Suspense fallback={<div></div>}>
                            <RiskRegistry />
                        </Suspense>
                    </div>
                )}
                 {/* {effectiveToggled.id11 && (
                    <div className='scroll_section' id="id11" ref={sectionsRef.id11}>
                        <Suspense fallback={<div></div>}>
                            <AIAdoptionWidget />
                        </Suspense>
                    </div>
                )} */}
                {/* {effectiveToggled.id4 && (
                    <div className='scroll_section' id="id4" ref={sectionsRef.id4}>
                        <PlanLockWrapper isLocked={isFreePlan}>
                            <Suspense fallback={<div></div>}>
                                <VulneralityManagement />
                            </Suspense>
                        </PlanLockWrapper>
                    </div>
                )} */}
                {/* {effectiveToggled.id8 && (
                    <div className='scroll_section' id="id8" ref={sectionsRef.id8}>
                        <Suspense fallback={<div></div>}>
                            <PolicyCenter />
                        </Suspense>
                    </div>
                )} */}
                {effectiveToggled.id9 && (
                    <div className='scroll_section' id="id9" ref={sectionsRef.id9}>
                        <Suspense fallback={<div></div>}>
                            <AddOns />
                        </Suspense>
                    </div>
                )}
            </div>

            {/* {userProfileUpdateModal && (
                <Suspense fallback={<div></div>}>
                    <UserProfileUpdateModal
                        show={userProfileUpdateModal}
                        onHide={() => {
                            setUserProfileUpdateModal(false);
                            localStorage.setItem("is_popup_closed", "true");
                        }}
                        backdrop="static"
                        keyboard={false}
                    />
                </Suspense>
            )} */}
        </div>
    );
}

export default Tabs;